import React, { useRef, useState } from "react";
import { ArrowRight, FileCheck2, Upload } from "lucide-react";
import { toast } from "sonner";
import { finApi, finErr, money } from "@/lib/financeApi";
import { btnGold, Chip, Empty, ErrorBox, Loading, useLoad, when } from "@/components/nds/windows/finance/parts";

export const STATUS_CHIP = { reading: ["yellow", "Reading…"], ready: ["green", "Ready to review"], failed: ["red", "Couldn't read"] };

/** Home card: open the monthly statement check. */
export const StatementCard = ({ onOpen }) => (
  <button type="button" onClick={onOpen} data-testid="flow-open-statements" className="w-full text-left p-5 rounded-md border border-violet-300/30 bg-violet-400/[0.05] hover:bg-violet-400/10 transition-colors flex items-center gap-4">
    <FileCheck2 className="h-6 w-6 text-violet-200" />
    <div className="flex-1"><div className="text-nex-silver font-semibold">Monthly statement check</div><div className="text-xs text-nex-muted">Upload last month's statement PDF — it's the source of truth; fix whatever doesn't match</div></div>
    <ArrowRight className="h-4 w-4 text-violet-200" />
  </button>
);

/** Envelope, staff and program spending added from statements, by month and category. */
function Spending({ from, to }) {
  const [s] = useLoad(() => finApi.get("/statements/spending"), []);
  const months = (s.data?.months || []).filter((m) => m.month >= from.slice(0, 7) && m.month <= to.slice(0, 7));
  if (!months.length) return null;
  return (
    <section className="space-y-2" data-testid="flow-spending">
      <h3 className="font-mono text-[10px] tracking-[0.2em] uppercase text-nex-gold">Program & staff spending · from statements</h3>
      <div className="grid gap-2.5 sm:grid-cols-2">{months.map((m) => (
        <div key={m.month} data-testid={`flow-spending-${m.month}`} className="p-4 rounded-md border border-white/10 bg-white/[0.02] space-y-1">
          <div className="flex items-center"><span className="text-nex-silver font-medium flex-1">{m.month}</span><span className="font-mono text-nex-silver">{money(m.total)}</span></div>
          {m.categories.map((c) => <div key={c.category} className="flex gap-2 text-xs text-nex-muted"><span className="flex-1 truncate">{c.category}</span><span className="font-mono">{money(c.amount)}</span></div>)}
        </div>))}</div>
    </section>
  );
}

/** Upload a statement PDF and pick one to review. */
export function StatementList({ from, to, onOpen }) {
  const [s, load] = useLoad(() => finApi.get("/statements"), []);
  const [all, setAll] = useState(false);
  const inside = (x) => all || (x.header?.month >= from.slice(0, 7) && x.header?.month <= to.slice(0, 7));
  const items = (s.data?.items || []).filter(inside);
  const hidden = (s.data?.items || []).length - items.length;
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try { const d = await finApi.upload("/statements", file); toast.success(`Uploaded · reading ${d.header.month}`); load(); onOpen(d); }
    catch (e) { toast.error(finErr(e)); } finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  return (
    <div className="space-y-5" data-testid="flow-statements">
      <div className="p-5 rounded-md border border-dashed border-violet-300/40 bg-violet-400/[0.04] space-y-3">
        <div className="text-nex-silver font-semibold">Upload a monthly statement</div>
        <p className="text-xs text-nex-muted">Use the "TEA Monthly Financial Statement" PDF from the old site. AI reads every itemized line, then each line is matched to the app by its reference number, or by resident, date and amount. Nothing changes until you act on a line.</p>
        <input ref={ref} type="file" accept="application/pdf" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="flow-statement-file" />
        <button type="button" disabled={busy} onClick={() => ref.current?.click()} data-testid="flow-statement-upload" className={`${btnGold} inline-flex items-center gap-1.5`}><Upload className="h-3.5 w-3.5" />{busy ? "Uploading…" : "Choose PDF"}</button>
      </div>
      {s.loading && !s.data ? <Loading testid="flow-statements-loading" /> : s.error ? <ErrorBox testid="flow-statements-error" error={s.error} onRetry={load} />
        : !items.length ? <Empty testid="flow-statements-empty">No statement covers {from} → {to}.{hidden ? " " : ""}{hidden ? <button type="button" onClick={() => setAll(true)} data-testid="flow-statements-show-all" className="text-nex-gold hover:underline">Show all {hidden}</button> : null}</Empty>
          : <div className="grid gap-2.5 sm:grid-cols-2">{items.map((x) => {
            const [tone, label] = STATUS_CHIP[x.status] || ["grey", x.status];
            return (
              <button key={x.id} type="button" onClick={() => onOpen(x)} data-testid={`flow-statement-${x.id}`} className="text-left p-4 rounded-md border border-white/10 bg-white/[0.02] hover:border-nex-gold/40 transition-colors space-y-1.5 min-w-0">
                <div className="flex items-center gap-2"><span className="text-nex-silver font-medium flex-1 truncate">{x.header.month} · {x.filename}</span>{x.locked && <Chip tone="green">Locked</Chip>}<Chip tone={tone}>{label}</Chip></div>
                <div className="text-[11px] text-nex-muted font-mono">Uploaded {when(x.created_at)} · {x.uploaded_by?.name || "—"}</div>
              </button>);
          })}</div>}
      <Spending from={from} to={to} />
    </div>
  );
}
